import React, { useState, useMemo } from 'react';
import { formatWeight, epley1RM } from '../utils/calculations';
import { deleteLogEntry, updateLogEntry, setProgramProgress, deletePR, getAllPRs, addPR, getPRsByLift } from '../db';
import { getProgramById } from '../data/programs';

export default function LogbookTab({ logEntries, prs, settings, progress, refresh }) {
  const [filterLift, setFilterLift] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [expandedCycleKey, setExpandedCycleKey] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [editNotes, setEditNotes] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const liftColors = {
    bench: 'var(--bench-color)',
    squat: 'var(--squat-color)',
    deadlift: 'var(--deadlift-color)',
  };

  // Group entries by lift and cycle
  const { currentCycleEntries, completedCycles } = useMemo(() => {
    const lifts = filterLift === 'all' ? ['bench', 'squat', 'deadlift'] : [filterLift];
    const currentEntries = [];
    const completed = [];

    for (const lift of lifts) {
      const liftEntries = logEntries.filter(e => e.lift === lift);
      const currentCycle = progress[lift]?.cycle || 1;
      const currentProgId = settings[`${lift}Program`];

      // Current cycle: entries matching current cycle number and program
      const current = liftEntries.filter(e =>
        e.cycle === currentCycle && e.programId === currentProgId
      );
      currentEntries.push(...current);

      // Group completed entries by cycle
      const pastEntries = liftEntries.filter(e =>
        !(e.cycle === currentCycle && e.programId === currentProgId)
      );

      // Group by cycle+programId
      const cycleMap = {};
      for (const entry of pastEntries) {
        const key = `${lift}_${entry.programId}_${entry.cycle || 1}`;
        if (!cycleMap[key]) {
          cycleMap[key] = {
            key,
            lift,
            programName: entry.programName || 'Unknown',
            programId: entry.programId,
            cycle: entry.cycle || 1,
            entries: [],
            completionDate: entry.date,
          };
        }
        cycleMap[key].entries.push(entry);
        // Track latest date as completion date
        if (entry.date > cycleMap[key].completionDate) {
          cycleMap[key].completionDate = entry.date;
        }
      }

      completed.push(...Object.values(cycleMap));
    }

    // Sort current entries - newest first in all views
    if (filterLift === 'all') {
      currentEntries.sort((a, b) => b.date.localeCompare(a.date) || b.timestamp - a.timestamp);
    } else {
      // Reverse program order for single lift (most recent session at top)
      currentEntries.sort((a, b) => {
        const wA = a.weekNum || 0, wB = b.weekNum || 0;
        const dA = a.dayNum || 0, dB = b.dayNum || 0;
        return wB - wA || dB - dA;
      });
    }

    // Sort completed cycles by completion date, newest first
    completed.sort((a, b) => b.completionDate.localeCompare(a.completionDate));

    // Sort entries within completed cycles - newest first
    for (const cycle of completed) {
      cycle.entries.sort((a, b) => {
        const wA = a.weekNum || 0, wB = b.weekNum || 0;
        const dA = a.dayNum || 0, dB = b.dayNum || 0;
        return wB - wA || dB - dA;
      });
    }

    return { currentCycleEntries: currentEntries, completedCycles: completed };
  }, [logEntries, filterLift, progress, settings]);

  // === Edit handlers ===
  const startEdit = (entry) => {
    const data = {};
    entry.exercises?.forEach((ex, i) => {
      data[i] = { weight: ex.loggedWeight || '', reps: ex.loggedReps || '', rpe: ex.rpe || '' };
    });
    setEditData(data);
    setEditNotes(entry.notes || '');
    setEditingId(entry.id);
  };

  const handleEditChange = (idx, field, value) => {
    setEditData(prev => ({
      ...prev,
      [idx]: { ...prev[idx], [field]: value }
    }));
  };

  const handleSaveEdit = async (entry) => {
    if (savingEdit) return;
    setSavingEdit(true);
    try {
      const updatedExercises = entry.exercises.map((ex, i) => ({
        ...ex,
        loggedWeight: parseFloat(editData[i]?.weight) || 0,
        loggedReps: parseInt(editData[i]?.reps) || 0,
        rpe: editData[i]?.rpe || null,
      }));

      // Recalculate estimated 1RM
      let estimatedMax = null;
      for (const ex of updatedExercises) {
        if ((ex.isAMAP || ex.isMaxTest) && ex.loggedWeight > 0 && ex.loggedReps > 0) {
          const est = epley1RM(ex.loggedWeight, ex.loggedReps);
          if (!estimatedMax || est > estimatedMax) estimatedMax = est;
        }
      }

      // Recalculate tonnage
      let tonnage = 0;
      for (const ex of updatedExercises) {
        if (ex.loggedWeight > 0 && ex.loggedReps > 0) {
          tonnage += ex.loggedWeight * ex.loggedReps * (ex.loggedSets || ex.prescribedSets || 1);
        }
      }

      // Check if this creates or removes a PR
      let isPR = false;
      if (estimatedMax) {
        const liftPRs = await getPRsByLift(entry.lift);
        const bestPR = liftPRs.reduce((max, pr) => Math.max(max, pr.estimatedMax), 0);
        const oneRM = settings[`${entry.lift}1RM`] || 0;
        if (estimatedMax > bestPR && estimatedMax > oneRM) {
          isPR = true;
          // Remove old PRs from this date/lift, add new one
          for (const pr of liftPRs) {
            if (pr.date === entry.date) await deletePR(pr.id);
          }
          await addPR({
            lift: entry.lift,
            date: entry.date,
            estimatedMax,
            achievedWith: updatedExercises.find(e => e.isAMAP || e.isMaxTest),
          });
        }
      }

      await updateLogEntry({
        ...entry,
        exercises: updatedExercises,
        estimatedMax,
        tonnage,
        isPR,
        notes: editNotes,
      });

      setEditingId(null);
      refresh();
    } finally {
      setSavingEdit(false);
    }
  };

  // === Delete handler ===
  const handleDelete = async (entry) => {
    if (deleting) return;
    setDeleting(true);
    try {
      await deleteLogEntry(entry.id);

      const allPRs = await getAllPRs();
      for (const pr of allPRs) {
        if (pr.lift === entry.lift && pr.date === entry.date) {
          await deletePR(pr.id);
        }
      }

      // Recalculate program progress from remaining logged sessions
      const lift = entry.lift;
      const programId = settings[`${lift}Program`];
      const program = getProgramById(programId);

      if (program && entry.programId === programId) {
        const currentProgress = progress[lift] || { weekIndex: 0, dayIndex: 0, cycle: 1 };
        const currentCycle = currentProgress.cycle;

        // Get remaining entries for this lift, program, and cycle
        const remaining = logEntries.filter(e =>
          e.id !== entry.id &&
          e.lift === lift &&
          e.programId === programId &&
          e.cycle === currentCycle
        );

        // Build a set of completed (weekNum, dayNum) pairs
        const completed = new Set();
        for (const e of remaining) {
          completed.add(`${e.weekNum}-${e.dayNum}`);
        }

        // Walk through the program to find the first uncompleted session
        let foundNext = false;
        for (let wi = 0; wi < program.weeks.length; wi++) {
          const week = program.weeks[wi];
          for (let di = 0; di < week.days.length; di++) {
            const day = week.days[di];
            const key = `${week.weekNum}-${day.dayNum}`;
            if (!completed.has(key)) {
              await setProgramProgress(lift, {
                ...currentProgress,
                weekIndex: wi,
                dayIndex: di,
              });
              foundNext = true;
              break;
            }
          }
          if (foundNext) break;
        }

        // If all sessions are still completed, progress is at the end
        if (!foundNext) {
          await setProgramProgress(lift, {
            ...currentProgress,
            weekIndex: program.weeks.length,
            dayIndex: 0,
          });
        }
      }

      setConfirmDeleteId(null);
      setExpandedId(null);
      refresh();
    } finally {
      setDeleting(false);
    }
  };

  // === Render entry ===
  const renderEntry = (entry) => {
    const isExpanded = expandedId === entry.id;
    const isEditing = editingId === entry.id;

    return (
      <div key={entry.id} className="log-entry"
        onClick={() => { if (!isEditing) setExpandedId(isExpanded ? null : entry.id); }}>
        <div className="log-entry-header">
          <div>
            <span className="log-entry-lift" style={{ color: liftColors[entry.lift] }}>
              {entry.lift?.charAt(0).toUpperCase() + entry.lift?.slice(1)}
            </span>
            {entry.isPR && <span className="pr-badge">PR</span>}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="log-entry-date">{fmtDate(entry.date)}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>W{entry.weekNum} D{entry.dayNum}</div>
          </div>
        </div>
        <div className="log-entry-summary">
          {entry.programName}
          {entry.tonnage > 0 && ` · ${(entry.tonnage / 1000).toFixed(1)}t`}
          {entry.estimatedMax && ` · Est. 1RM: ${formatWeight(entry.estimatedMax)}kg`}
        </div>

        {isExpanded && !isEditing && (
          <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}
            onClick={e => e.stopPropagation()}>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Exercise</th><th>Weight</th><th>Reps</th><th>Sets</th>
                    {entry.exercises?.some(e => e.rpe) && <th>RPE</th>}
                  </tr>
                </thead>
                <tbody>
                  {entry.exercises?.map((ex, i) => (
                    <tr key={i} style={{ opacity: ex.isAccessory ? 0.7 : 1 }}>
                      <td style={{ whiteSpace: 'normal', minWidth: 120 }}>
                        {ex.name}
                        {ex.isAMAP && <span className="amap-badge" style={{ marginLeft: 4, fontSize: 9 }}>AMAP</span>}
                      </td>
                      <td>{ex.loggedWeight > 0 ? `${formatWeight(ex.loggedWeight)}kg` : '—'}</td>
                      <td>{ex.loggedReps || '—'}</td>
                      <td>{ex.loggedSets || ex.prescribedSets || '—'}</td>
                      {entry.exercises?.some(e => e.rpe) && <td>{ex.rpe || '—'}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {entry.tonnage > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-dim)' }}>
                Tonnage: {(entry.tonnage / 1000).toFixed(1)}t ({entry.tonnage.toLocaleString()}kg)
              </div>
            )}
            {entry.notes && (
              <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-dim)', fontStyle: 'italic' }}>
                {entry.notes}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn btn-secondary btn-small" style={{ flex: 1 }}
                onClick={() => startEdit(entry)}>
                Edit
              </button>
              <button className="btn btn-danger btn-small" style={{ flex: 1 }}
                onClick={() => setConfirmDeleteId(entry.id)}>
                Delete
              </button>
            </div>
          </div>
        )}

        {isExpanded && isEditing && (
          <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}
            onClick={e => e.stopPropagation()}>
            <div className="card-header">Edit Results</div>
            {entry.exercises?.map((ex, i) => (
              <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                  {ex.name} {ex.isAMAP && <span className="amap-badge" style={{ fontSize: 9 }}>AMAP</span>}
                </div>
                <div className="set-log-row" style={{ borderBottom: 'none' }}>
                  <span className="set-label">Weight</span>
                  <input type="number" value={editData[i]?.weight ?? ''} placeholder={ex.prescribedWeight || ''}
                    onChange={e => handleEditChange(i, 'weight', e.target.value)}
                    onFocus={e => e.target.select()} />
                  <span style={{ color: 'var(--text-dim)', fontSize: 13 }}>kg</span>
                  <span className="set-label" style={{ marginLeft: 12 }}>Reps</span>
                  <input type="number" value={editData[i]?.reps ?? ''} placeholder={ex.prescribedReps || ''}
                    onChange={e => handleEditChange(i, 'reps', e.target.value)}
                    onFocus={e => e.target.select()} />
                </div>
                {ex.isRPE && (
                  <div style={{ marginTop: 4 }}>
                    <span className="set-label">RPE</span>
                    <div className="rpe-selector">
                      {[6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10].map(v => (
                        <button key={v} className={`rpe-btn ${editData[i]?.rpe === v ? 'selected' : ''}`}
                          onClick={() => handleEditChange(i, 'rpe', v)}>{v}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div style={{ padding: '12px 0' }}>
              <label>Notes</label>
              <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)}
                placeholder="Session notes..." />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-small" style={{ flex: 1 }}
                onClick={() => handleSaveEdit(entry)} disabled={savingEdit}>
                {savingEdit ? 'Saving...' : 'Save'}
              </button>
              <button className="btn btn-secondary btn-small" style={{ flex: 1 }}
                onClick={() => setEditingId(null)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const filteredCount = filterLift === 'all'
    ? logEntries.length
    : logEntries.filter(e => e.lift === filterLift).length;
  const filteredLabel = filterLift === 'all' ? '' : ` ${filterLift}`;

  return (
    <div>
      <div className="session-header">
        <h1>Logbook</h1>
        <p className="sub">{filteredCount}{filteredLabel} session{filteredCount !== 1 ? 's' : ''} logged</p>
      </div>

      {/* Lift filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {['all', 'bench', 'squat', 'deadlift'].map(f => (
          <button key={f} onClick={() => { setFilterLift(f); setExpandedId(null); setEditingId(null); setExpandedCycleKey(null); }}
            style={{
              flex: 1, padding: '8px 0', borderRadius: 8,
              border: `1px solid ${filterLift === f ? 'var(--accent)' : 'var(--border)'}`,
              background: filterLift === f ? 'var(--bg-elevated)' : 'none',
              color: filterLift === f ? (f === 'all' ? 'var(--accent)' : liftColors[f]) : 'var(--text-dim)',
              fontFamily: 'Barlow Condensed, sans-serif', fontSize: 13, fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer',
            }}>
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {logEntries.length === 0 ? (
        <div className="empty-state">
          <h3>No sessions yet</h3>
          <p>Log your first session from the Home tab.</p>
        </div>
      ) : (
        <>
          {/* Current cycle entries */}
          {currentCycleEntries.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div className="card-header">Current Cycle</div>
              {currentCycleEntries.map(renderEntry)}
            </div>
          )}

          {currentCycleEntries.length === 0 && (
            <div style={{ textAlign: 'center', padding: '12px 0 20px', color: 'var(--text-muted)', fontSize: 13 }}>
              No sessions logged in current cycle yet
            </div>
          )}

          {/* Completed cycles */}
          {completedCycles.length > 0 && (
            <div>
              <div className="card-header">Completed Cycles</div>
              {completedCycles.map(cycle => {
                const isExpCycle = expandedCycleKey === cycle.key;
                return (
                  <div key={cycle.key} style={{ marginBottom: 8 }}>
                    <div className="completed-cycle-header"
                      onClick={() => setExpandedCycleKey(isExpCycle ? null : cycle.key)}>
                      <div style={{ flex: 1 }}>
                        <span style={{ color: liftColors[cycle.lift], fontWeight: 600, fontFamily: 'Barlow Condensed, sans-serif', fontSize: 14, textTransform: 'uppercase' }}>
                          {cycle.lift}
                        </span>
                        <span style={{ color: 'var(--text-dim)', fontSize: 13, marginLeft: 8 }}>
                          {cycle.programName}
                        </span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                          Cycle {cycle.cycle} · {cycle.entries.length} session{cycle.entries.length !== 1 ? 's' : ''}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {fmtDate(cycle.completionDate)}
                        </div>
                      </div>
                      <span style={{ color: 'var(--text-muted)', fontSize: 16, marginLeft: 12 }}>
                        {isExpCycle ? '−' : '+'}
                      </span>
                    </div>

                    {isExpCycle && (
                      <div style={{ marginTop: 4 }}>
                        {cycle.entries.map(renderEntry)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Confirm delete modal */}
      {confirmDeleteId && (
        <div className="modal-overlay" onClick={() => setConfirmDeleteId(null)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <h3>Delete Workout?</h3>
            <p>This will permanently delete this session and roll back your program position. This cannot be undone.</p>
            <div className="btn-row">
              <button className="btn btn-secondary" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
              <button className="btn btn-danger" disabled={deleting}
                onClick={() => {
                  const entry = logEntries.find(e => e.id === confirmDeleteId);
                  if (entry) handleDelete(entry);
                }}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}
